/**
 * @file useCustomerOverview.ts
 * @description Сводка клиента для «Обзора» карточки (phase19.31, SC68):
 * загрузка, повтор после сбоя и отметка выполнения задачи флажком.
 *
 * Перечитывание не прячет сводку — прежние данные остаются на экране, пока
 * идёт запрос. После выполнения задачи сводку перечитываем (а не правим
 * руками): просроченная задача уходит и из «Требует внимания».
 */

import { useCallback, useEffect, useState } from "react";

import { useNotify } from "../../ui/NotifyProvider";
import { completeTaskWithUndo } from "../../crm/tasks/task-mutations";
import { customerOverview, type CustomerOverviewDto } from "../../../lib/api-customers-overview";

export interface CustomerOverviewState {
  overview: CustomerOverviewDto | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Задачи, которые сейчас отмечаются выполненными. */
  completing: ReadonlySet<string>;
  completeTask: (taskId: string) => void;
}

/** Текст сбоя сводки — один на блоки «Обзора». */
export const OVERVIEW_ERROR_TEXT = "Не удалось загрузить сводку";

export function useCustomerOverview(customerId: string | undefined): CustomerOverviewState {
  const notify = useNotify();
  const [overview, setOverview] = useState<CustomerOverviewDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [completing, setCompleting] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true);
    customerOverview
      .get(customerId)
      .then((data) => {
        if (cancelled) return;
        setOverview(data);
        setError(null);
      })
      .catch(() => {
        // Текст один для всех сбоев: причина в журнале сервера, менеджеру — «Повторить».
        if (!cancelled) setError(OVERVIEW_ERROR_TEXT);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, attempt]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  const completeTask = useCallback(
    (taskId: string) => {
      setCompleting((prev) => new Set(prev).add(taskId));
      // Уведомление с «Отменить» — возврат задачи в работу (контракт §13).
      void completeTaskWithUndo(taskId, { notify, reload: refresh }).finally(() =>
        setCompleting((prev) => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        }),
      );
    },
    [notify, refresh],
  );

  return { overview, loading, error, refresh, completing, completeTask };
}
